import { sectionRenderer } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';
import { debounce, formatMoney, startViewTransition } from '@theme/utilities';

/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    let newParameters = new URLSearchParams(/** @type any */ (formData));

    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    newParameters.delete('page');

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    return newParameters;
  }

  /**
   * Gets the search query parameter from the current URL
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  /**
   * Updates the URL hash with current filter parameters
   */
  #updateURLHash() {
    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();

    url.search = '';
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    history.pushState({ urlParameters: urlParameters.toString() }, '', url.toString());
  }

  /**
   * Updates filters and renders the section
   */
  updateFilters = () => {
    this.#updateURLHash();
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection();
  };

  /**
   * Updates the section
   */
  #updateSection() {
    // Avoid View Transitions on mobile: the View Transition layer can briefly render above modal dialogs,
    // which can cause the product grid to "peek" through loaders/overlays.
    const viewTransition = !this.closest('dialog') && window.innerWidth >= 750;

    if (viewTransition) {
      startViewTransition(() => sectionRenderer.renderSection(this.sectionId), ['product-grid']);
    } else {
      sectionRenderer.renderSection(this.sectionId);
    }
  }

  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   */
  updateFiltersByURL(url) {
    history.pushState('', '', url);
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection();
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   */
  updateFilters() {
    const facetsForm = this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#updateSelectedFacetSummary();
  }

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      this.updateFilters();
    }
  }

  /**
   * Handles mouseover events on facet labels
   * @param {MouseEvent} event - The mouseover event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const form = this.closest('form');
    if (!form) return;

    const formData = new FormData(form);
    const inputElement = event.target.querySelector('input');

    if (!(inputElement instanceof HTMLInputElement)) return;

    if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const urlParameters = facetsForm.createURLParameters(formData);

    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);

    sectionRenderer.getSectionHTML(this.sectionId, true, url);
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input
 * @property {HTMLInputElement} maxInput - The maximum price input
 */

/**
 * Handles price facet functionality
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
    
    // Initialize immediately if DOM is ready, otherwise delay
    if (this.refs.sliderTrack && this.refs.minThumb && this.refs.maxThumb) {
      this.#initializeSlider();
    } else {
      // Use MutationObserver to detect when elements are added
      this.#observeForElements();
    }
    
    // Listen for filter updates to preserve slider state
    this.addEventListener('filter-updated', this.#onFilterUpdated.bind(this));
    
    // Listen for re-initialization events
    this.addEventListener('reinitialize-slider', this.#onReinitializeSlider.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
    this.#removeSliderListeners();
    
    // Clean up observer
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  /**
   * Handles keydown events to restrict input to valid characters
   * @param {KeyboardEvent} event - The keydown event
   */
  #onKeyDown = (event) => {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  /**
   * Updates price filter and results
   */
  updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;

    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#setMinAndMaxValues();
    this.#updateSummary();
  }

  /**
   * Adjusts input values to be within valid range
   * @param {HTMLInputElement} input - The input element to adjust
   */
  #adjustToValidValues(input) {
    if (input.value.trim() === '') return;

    const value = Number(input.value);
    const min = Number(formatMoney(input.getAttribute('data-min') ?? ''));
    const max = Number(formatMoney(input.getAttribute('data-max') ?? ''));

    if (value < min) input.value = min.toString();
    if (value > max) input.value = max.toString();
  }

  /**
   * Sets min and max values for the inputs
   */
  #setMinAndMaxValues() {
    const { minInput, maxInput } = this.refs;

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', '0');
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max') ?? '');
  }

  /**
   * Updates the price summary
   */
  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent?.updatePriceSummary(minInput, maxInput);
  }

  /**
   * Initialize the price slider
   */
  #initializeSlider() {
    const { sliderTrack, minThumb, maxThumb, sliderRange, minLabel, maxLabel } = this.refs;
    const { minInput, maxInput } = this.refs;

    if (!sliderTrack || !minThumb || !maxThumb || !sliderRange) return;

    // Check if slider is already initialized to prevent re-initialization
    if (this.sliderInitialized) {
      // Re-initialize for AJAX updates
      this.#reinitializeSlider();
      return;
    }

    this.sliderTrack = sliderTrack;
    this.minThumb = minThumb;
    this.maxThumb = maxThumb;
    this.sliderRange = sliderRange;
    this.minLabel = minLabel;
    this.maxLabel = maxLabel;
    this.minInput = minInput;
    this.maxInput = maxInput;

    // Get price range - use the actual filter range_max
    this.minPrice = 0;
    
    // Get the actual range_max from multiple sources
    const rangeMaxElement = this.sliderTrack.closest('.price-facet').querySelector('[data-range-max]');
    const rangeMax = rangeMaxElement ? Number(rangeMaxElement.getAttribute('data-range-max')) : null;
    
    // Also try to get from maxThumb data-value (which should be the range_max)
    // Remove commas before converting to number to handle formatted values like '3,450.00'
    const maxThumbValue = Number(this.maxThumb.dataset.value.replace(/,/g, ''));
    
    // Prioritize data-range-max for the slider's maximum draggable range
    // The maxThumb data-value represents the current filter value, not the max range
    this.maxPrice = rangeMax || Math.max(
      maxThumbValue || 0,
      Number(this.maxInput.getAttribute('data-max')) || 0,
      1000
    );
    
    
    this.range = this.maxPrice - this.minPrice;
    
    // Read the current positions set by Liquid - don't override them
    const currentMin = Number(this.minThumb.dataset.value) || 0;
    const currentMax = Number(this.maxThumb.dataset.value) || this.maxPrice;
    
    // Store the current values for reference
    this.currentMinValue = currentMin;
    this.currentMaxValue = currentMax;
    
    // Don't override the Liquid-set positions, just update labels
    this.#updateSliderLabels();

    // Add event listeners
    this.#addSliderListeners();
    
    // Validate constraints after initialization
    this.#validateSliderConstraints();
    
    // Mark as initialized
    this.sliderInitialized = true;
  }

  /**
   * Re-initialize slider for AJAX updates
   */
  #reinitializeSlider() {
    const { sliderTrack, minThumb, maxThumb, sliderRange, minLabel, maxLabel } = this.refs;
    const { minInput, maxInput } = this.refs;

    if (!sliderTrack || !minThumb || !maxThumb || !sliderRange) return;

    // Update references
    this.sliderTrack = sliderTrack;
    this.minThumb = minThumb;
    this.maxThumb = maxThumb;
    this.sliderRange = sliderRange;
    this.minLabel = minLabel;
    this.maxLabel = maxLabel;
    this.minInput = minInput;
    this.maxInput = maxInput;

    // Get the actual range_max from multiple sources
    const rangeMaxElement = this.sliderTrack.closest('.price-facet').querySelector('[data-range-max]');
    const rangeMax = rangeMaxElement ? Number(rangeMaxElement.getAttribute('data-range-max')) : null;
    
    // Also try to get from maxThumb data-value (which should be the range_max)
    // Remove commas before converting to number to handle formatted values like '3,450.00'
    const maxThumbValue = Number(this.maxThumb.dataset.value.replace(/,/g, ''));
    
    // Prioritize data-range-max for the slider's maximum draggable range
    // The maxThumb data-value represents the current filter value, not the max range
    this.maxPrice = rangeMax || Math.max(
      maxThumbValue || 0,
      this.maxPrice || 0,
      1000
    );
    
    this.range = this.maxPrice - this.minPrice;
    
    // Read current values from Liquid-set positions
    const currentMin = Number(this.minThumb.dataset.value) || 0;
    const currentMax = Number(this.maxThumb.dataset.value) || this.maxPrice;
    
    // Update stored values
    this.currentMinValue = currentMin;
    this.currentMaxValue = currentMax;
    
    // Update labels
    this.#updateSliderLabels();
    
    // Re-add event listeners
    this.#removeSliderListeners();
    this.#addSliderListeners();
  }

  /**
   * Add slider event listeners
   */
  #addSliderListeners() {
    // Cache bound handlers so removeEventListener works correctly (important for AJAX re-inits)
    if (!this._priceSliderHandlers) {
      this._priceSliderHandlers = {
        minMouseDown: this.#onThumbMouseDown.bind(this, 'min'),
        maxMouseDown: this.#onThumbMouseDown.bind(this, 'max'),
        trackClick: this.#onTrackClick.bind(this),
        minTouchStart: this.#onThumbTouchStart.bind(this, 'min'),
        maxTouchStart: this.#onThumbTouchStart.bind(this, 'max'),
        trackTouchStart: this.#onTrackTouchStart.bind(this),
        touchMove: this.#onTouchMove.bind(this),
        touchEnd: this.#onTouchEnd.bind(this),
      };
    }

    // Desktop mouse
    this.minThumb.addEventListener('mousedown', this._priceSliderHandlers.minMouseDown);
    this.maxThumb.addEventListener('mousedown', this._priceSliderHandlers.maxMouseDown);
    this.sliderTrack.addEventListener('click', this._priceSliderHandlers.trackClick);

    // Touch devices
    this.minThumb.addEventListener('touchstart', this._priceSliderHandlers.minTouchStart, { passive: false });
    this.maxThumb.addEventListener('touchstart', this._priceSliderHandlers.maxTouchStart, { passive: false });
    this.sliderTrack.addEventListener('touchstart', this._priceSliderHandlers.trackTouchStart, { passive: false });
    
    // Input change listeners
    this.minInput.addEventListener('input', this.#onInputChange.bind(this, 'min'));
    this.maxInput.addEventListener('input', this.#onInputChange.bind(this, 'max'));
  }

  /**
   * Remove slider event listeners
   */
  #removeSliderListeners() {
    if (!this._priceSliderHandlers) return;

    this.minThumb?.removeEventListener('mousedown', this._priceSliderHandlers.minMouseDown);
    this.maxThumb?.removeEventListener('mousedown', this._priceSliderHandlers.maxMouseDown);
    this.sliderTrack?.removeEventListener('click', this._priceSliderHandlers.trackClick);

    this.minThumb?.removeEventListener('touchstart', this._priceSliderHandlers.minTouchStart);
    this.maxThumb?.removeEventListener('touchstart', this._priceSliderHandlers.maxTouchStart);
    this.sliderTrack?.removeEventListener('touchstart', this._priceSliderHandlers.trackTouchStart);

    document.removeEventListener('touchmove', this._priceSliderHandlers.touchMove);
    document.removeEventListener('touchend', this._priceSliderHandlers.touchEnd);
    document.removeEventListener('touchcancel', this._priceSliderHandlers.touchEnd);
  }

  /**
   * Core drag logic shared across mouse/touch
   * @param {number} clientX
   */
  #handleDragAtClientX(clientX) {
    if (!this.isDragging) return;

    const rect = this.sliderTrack.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    const value = Math.round(this.minPrice + (percentage / 100) * this.range);

    // Ensure value is valid
    if (isNaN(value) || value < this.minPrice || value > this.maxPrice) {
      return;
    }

    if (this.activeThumb === 'min') {
      const maxValue = this.#getThumbValue('max');
      if (value >= maxValue - 1) return; // Ensure at least 1 unit gap
      this.#setThumbValue('min', value);
    } else {
      const minValue = this.#getThumbValue('min');
      if (value <= minValue + 1) return; // Ensure at least 1 unit gap
      this.#setThumbValue('max', value);
    }

    this.#updateSliderRange();
    this.#updateInputsFromSlider();
    this.#updateSliderLabels();
  }

  /**
   * Handle thumb mouse down
   */
  #onThumbMouseDown = (thumb, event) => {
    event.preventDefault();
    this.activeThumb = thumb;
    this.isDragging = true;

    document.addEventListener('mousemove', this.#onMouseMove);
    document.addEventListener('mouseup', this.#onMouseUp);
  };

  /**
   * Handle mouse move during drag
   */
  #onMouseMove = (event) => {
    this.#handleDragAtClientX(event.clientX);
  };

  /**
   * Handle thumb touch start
   * @param {'min' | 'max'} thumb
   * @param {TouchEvent} event
   */
  #onThumbTouchStart(thumb, event) {
    // iOS needs passive:false to allow preventDefault (prevents page scroll while dragging)
    event.preventDefault();
    event.stopPropagation();

    const touch = event.touches?.[0];
    if (!touch) return;

    this.activeThumb = thumb;
    this.isDragging = true;
    this._activeTouchId = touch.identifier;

    document.addEventListener('touchmove', this._priceSliderHandlers.touchMove, { passive: false });
    document.addEventListener('touchend', this._priceSliderHandlers.touchEnd, { passive: false });
    document.addEventListener('touchcancel', this._priceSliderHandlers.touchEnd, { passive: false });
  }

  /**
   * Handle touch move during drag
   * @param {TouchEvent} event
   */
  #onTouchMove(event) {
    if (!this.isDragging) return;
    event.preventDefault();

    let touch = null;
    const touches = event.touches || [];
    for (let i = 0; i < touches.length; i++) {
      if (this._activeTouchId == null || touches[i].identifier === this._activeTouchId) {
        touch = touches[i];
        break;
      }
    }
    if (!touch) return;

    this.#handleDragAtClientX(touch.clientX);
  }

  /**
   * Handle touch end/cancel
   * @param {TouchEvent} event
   */
  #onTouchEnd(event) {
    if (!this.isDragging) return;

    // Only end when the active touch ends (if tracked)
    if (this._activeTouchId != null) {
      const changed = event.changedTouches || [];
      let ended = false;
      for (let i = 0; i < changed.length; i++) {
        if (changed[i].identifier === this._activeTouchId) {
          ended = true;
          break;
        }
      }
      if (!ended) return;
    }

    this.isDragging = false;
    this.activeThumb = null;
    this._activeTouchId = null;

    document.removeEventListener('touchmove', this._priceSliderHandlers.touchMove);
    document.removeEventListener('touchend', this._priceSliderHandlers.touchEnd);
    document.removeEventListener('touchcancel', this._priceSliderHandlers.touchEnd);

    this.updatePriceFilterAndResults();
  }

  /**
   * Allow dragging from the track on touch (much easier on phones)
   * @param {TouchEvent} event
   */
  #onTrackTouchStart(event) {
    event.preventDefault();

    const touch = event.touches?.[0];
    if (!touch) return;

    // Choose the closest thumb to the touch point, then start dragging that thumb.
    const rect = this.sliderTrack.getBoundingClientRect();
    const x = touch.clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    const value = Math.round(this.minPrice + (percentage / 100) * this.range);

    const minValue = this.#getThumbValue('min');
    const maxValue = this.#getThumbValue('max');
    const distanceToMin = Math.abs(value - minValue);
    const distanceToMax = Math.abs(value - maxValue);

    this.activeThumb = distanceToMin < distanceToMax ? 'min' : 'max';
    this.isDragging = true;
    this._activeTouchId = touch.identifier;

    // Apply initial move immediately
    this.#handleDragAtClientX(touch.clientX);

    document.addEventListener('touchmove', this._priceSliderHandlers.touchMove, { passive: false });
    document.addEventListener('touchend', this._priceSliderHandlers.touchEnd, { passive: false });
    document.addEventListener('touchcancel', this._priceSliderHandlers.touchEnd, { passive: false });
  }

  /**
   * Handle mouse up
   */
  #onMouseUp = () => {
    this.isDragging = false;
    this.activeThumb = null;
    document.removeEventListener('mousemove', this.#onMouseMove);
    document.removeEventListener('mouseup', this.#onMouseUp);
    
    // Update filters after drag ends
    this.updatePriceFilterAndResults();
  };

  /**
   * Handle track click
   */
  #onTrackClick = (event) => {
    const rect = this.sliderTrack.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    const value = Math.round(this.minPrice + (percentage / 100) * this.range);

    const minValue = this.#getThumbValue('min');
    const maxValue = this.#getThumbValue('max');
    const distanceToMin = Math.abs(value - minValue);
    const distanceToMax = Math.abs(value - maxValue);

    if (distanceToMin < distanceToMax) {
      this.#setThumbValue('min', Math.min(value, maxValue - 1));
    } else {
      this.#setThumbValue('max', Math.max(value, minValue + 1));
    }

    this.#updateSliderRange();
    this.#updateInputsFromSlider();
    this.#updateSliderLabels();
    this.updatePriceFilterAndResults();
  };

  /**
   * Handle input change
   */
  #onInputChange = (inputType) => {
    this.#updateSliderFromInputs();
    this.#updateSliderRange();
    this.#updateSliderLabels();
  };

  /**
   * Get thumb value
   */
  #getThumbValue(thumb) {
    if (thumb === 'min') {
      return this.currentMinValue || Number(this.minThumb.dataset.value) || 0;
    } else {
      return this.currentMaxValue || Number(this.maxThumb.dataset.value) || this.maxPrice;
    }
  }

  /**
   * Set thumb value
   */
  #setThumbValue(thumb, value) {
    const thumbElement = thumb === 'min' ? this.minThumb : this.maxThumb;
    
    // Get current values to enforce constraints
    const currentMin = this.#getThumbValue('min');
    const currentMax = this.#getThumbValue('max');
    
    // Enforce minimum gap and prevent crossing
    if (thumb === 'min') {
      // Min thumb cannot exceed max thumb minus 1 unit
      const maxAllowed = currentMax - 1;
      value = Math.min(value, maxAllowed);
    } else {
      // Max thumb cannot go below min thumb plus 1 unit
      const minAllowed = currentMin + 1;
      value = Math.max(value, minAllowed);
    }
    
    // Ensure value is within bounds
    value = Math.max(this.minPrice, Math.min(this.maxPrice, value));
    
    const percentage = ((value - this.minPrice) / this.range) * 100;
    thumbElement.style.left = `${Math.max(0, Math.min(100, percentage))}%`;
    thumbElement.dataset.value = value;
    
    // Update stored values
    if (thumb === 'min') {
      this.currentMinValue = value;
    } else {
      this.currentMaxValue = value;
    }
  }

  /**
   * Validate and fix slider constraints
   */
  #validateSliderConstraints() {
    const minValue = this.#getThumbValue('min');
    const maxValue = this.#getThumbValue('max');
    
    // If min is greater than or equal to max, fix it
    if (minValue >= maxValue) {
      // Reset to safe values
      const safeMin = Math.max(this.minPrice, maxValue - 1);
      const safeMax = Math.min(this.maxPrice, minValue + 1);
      
      this.#setThumbValue('min', safeMin);
      this.#setThumbValue('max', safeMax);
      
      console.warn('Slider constraints violated, reset to safe values:', {
        originalMin: minValue,
        originalMax: maxValue,
        newMin: safeMin,
        newMax: safeMax
      });
    }
  }

  /**
   * Update slider range
   */
  #updateSliderRange() {
    const minValue = this.#getThumbValue('min');
    const maxValue = this.#getThumbValue('max');
    const minPercentage = ((minValue - this.minPrice) / this.range) * 100;
    const maxPercentage = ((maxValue - this.minPrice) / this.range) * 100;

    this.sliderRange.style.left = `${minPercentage}%`;
    this.sliderRange.style.width = `${maxPercentage - minPercentage}%`;
  }

  /**
   * Update slider from input values
   */
  #updateSliderFromInputs() {
    const minValue = Number(this.minInput.value) || this.minPrice;
    const maxValue = Number(this.maxInput.value) || this.maxPrice;

    // Ensure minimum gap between thumbs
    const safeMin = Math.max(this.minPrice, Math.min(maxValue - 1, minValue));
    const safeMax = Math.min(this.maxPrice, Math.max(minValue + 1, maxValue));

    this.#setThumbValue('min', safeMin);
    this.#setThumbValue('max', safeMax);
  }

  /**
   * Update inputs from slider values
   */
  #updateInputsFromSlider() {
    const minValue = this.#getThumbValue('min');
    const maxValue = this.#getThumbValue('max');

    this.minInput.value = minValue;
    this.maxInput.value = maxValue;
  }

  /**
   * Update slider labels
   */
  #updateSliderLabels() {
    const minValue = this.#getThumbValue('min');
    const maxValue = this.#getThumbValue('max');

    if (this.minLabel) {
      this.minLabel.textContent = this.#formatMoney(minValue);
    }
    if (this.maxLabel) {
      this.maxLabel.textContent = this.#formatMoney(maxValue);
    }
  }

  /**
   * Format money value
   */
  #formatMoney(value) {
    // Use Shopify's money formatting
    const currency = window.Shopify?.currency?.active || 'INR';
    const symbol = window.Shopify?.currency?.symbol || '₹';
    return `${symbol}${value.toLocaleString()}`;
  }

  /**
   * Handle filter updates to preserve slider state
   */
  #onFilterUpdated() {
    console.log('Filter updated, preserving slider state...');
    
    // Get current slider values
    const currentMin = this.#getThumbValue('min');
    const currentMax = this.#getThumbValue('max');
    
    // Update inputs to match slider
    if (this.minInput) this.minInput.value = currentMin;
    if (this.maxInput) this.maxInput.value = currentMax;
    
    // Update labels
    this.#updateSliderLabels();
  }

  /**
   * Handle re-initialization events
   */
  #onReinitializeSlider() {
    console.log('Re-initializing slider due to AJAX update...');
    this.#reinitializeSlider();
  }

  /**
   * Observe for DOM elements to be added
   */
  #observeForElements() {
    if (this.observer) {
      this.observer.disconnect();
    }

    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          // Check if slider elements are now available
          if (this.refs.sliderTrack && this.refs.minThumb && this.refs.maxThumb) {
            console.log('Slider elements detected, initializing...');
            this.#initializeSlider();
            this.observer.disconnect();
            this.observer = null;
          }
        }
      });
    });

    // Start observing the component for changes
    this.observer.observe(this, {
      childList: true,
      subtree: true
    });

    // Fallback timeout
    setTimeout(() => {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
        // Try to initialize anyway
        if (this.refs.sliderTrack && this.refs.minThumb && this.refs.maxThumb) {
          this.#initializeSlider();
        }
      }
    }, 1000);
  }
}

if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}

// Listen for facet updates to re-initialize price sliders
document.addEventListener('DOMContentLoaded', () => {
  // Listen for facet form updates
  const facetsForm = document.querySelector('facets-form-component');
  if (facetsForm) {
    facetsForm.addEventListener('filter-updated', () => {
      console.log('Facet filter updated, re-initializing price sliders...');
      // Find all price facet components and re-initialize them
      const priceFacets = document.querySelectorAll('price-facet-component');
      priceFacets.forEach(facet => {
        if (facet.sliderInitialized) {
          // Dispatch a custom event to trigger re-initialization
          facet.dispatchEvent(new CustomEvent('reinitialize-slider'));
        }
      });
    });
  }
});

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component');
    container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.value = '';
      }
    });

    const details = event.target.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.clearSummary();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === 'Enter') this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('facets__clear--active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const url = this.dataset.url;
    if (!url) return;

    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFiltersByURL(url);
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 * @property {HTMLDetailsElement} details - The details element
 * @property {HTMLElement} summary - The summary element
 * @property {HTMLElement} listbox - The listbox element
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  requiredRefs = ['details', 'summary', 'listbox'];

  /**
   * Handles keyboard navigation in the sorting dropdown
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    const { listbox } = this.refs;
    if (!(listbox instanceof Element)) return;

    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'Enter':
      case ' ':
        if (event.target instanceof Element) {
          const targetOption = event.target.closest('[role="option"]');
          if (targetOption) {
            event.preventDefault();
            this.#selectOption(targetOption);
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Handles details toggle event
   */
  handleToggle = () => {
    const { details, summary, listbox } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;

    const isOpen = details.open;
    summary.setAttribute('aria-expanded', isOpen.toString());

    if (isOpen && listbox instanceof Element) {
      // Move focus to selected option when dropdown opens
      const selectedOption = listbox.querySelector('[aria-selected="true"]');
      if (selectedOption instanceof HTMLElement) {
        selectedOption.focus();
      }
    }
  };

  /**
   * Moves focus between options
   * @param {Element[]} options - The option elements
   * @param {number} newIndex - The index of the option to focus
   */
  #moveFocus(options, newIndex) {
    // Remove tabindex from all options
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    // Set tabindex and focus on new option
    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * Selects an option and triggers form submission
   * @param {Element} option - The option element to select
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
      // Update aria-selected states
      this.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });
      option.setAttribute('aria-selected', 'true');

      // Trigger click on the input to ensure normal form behavior
      input.click();

      // Close dropdown and return focus (handles tabIndex reset)
      this.#closeDropdown();
    }
  }

  /**
   * Closes the dropdown and returns focus to summary
   */
  #closeDropdown() {
    const { details, summary } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      // Reset focus to match the actual selected option
      const options = this.querySelectorAll('[role="option"]');
      const selectedOption = this.querySelector('[aria-selected="true"]');

      options.forEach((opt) => {
        if (opt instanceof HTMLElement) {
          opt.tabIndex = -1;
        }
      });

      if (selectedOption instanceof HTMLElement) {
        selectedOption.tabIndex = 0;
      }

      details.open = false;
      if (summary instanceof HTMLElement) {
        summary.focus();
      }
    }
  }

  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    const facetsForm =
      this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;
    const isMobile = window.innerWidth < 750;

    const shouldDisable = this.dataset.shouldUseSelectOnMobile === 'true';

    // Because we have a select element on mobile and a bunch of radio buttons on desktop,
    // we need to disable the input during "form-submission" to prevent duplicate entries.
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = true;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = true;
      }
    }

    facetsForm.updateFilters();
    this.updateFacetStatus(event);

    // Re-enable the input after the form-submission
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = false;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = false;
      }
    }

    // Close the details element when a value is selected
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = false;
  }

  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const details = this.querySelector('details');
    if (!details) return;

    const facetStatus = details.querySelector('facet-status-component');
    if (!(facetStatus instanceof FacetStatusComponent)) return;

    facetStatus.textContent =
      event.target.value !== details.dataset.defaultSortBy ? event.target.dataset.optionName ?? '' : '';
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('bubble', 'facets__bubble');
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector('span.swatch');
        return swatch?.outerHTML ?? '';
      })
      .join('');
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount === 1) {
      facetStatus.innerHTML = checkedInputElements[0]?.dataset.label ?? '';
      return;
    }

    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add('bubble', 'facets__bubble');
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = '';
      return;
    }

    const minInputNum = this.#parseCents(minInputValue, '0');
    const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax);
    facetStatus.innerHTML = `${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}`;
  }

  /**
   * Parses a decimal number as cents
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid
   * @returns {number} The money value in cents
   */
  #parseCents(value, fallback = '0') {
    const parts = value ? value.trim().split(/[^0-9]/) : (parseInt(fallback, 10) / 100).toString();
    const [wholeStr, fractionStr, ...rest] = parts;
    if (typeof wholeStr !== 'string' || rest.length > 0) return parseInt(fallback, 10);

    const whole = parseInt(wholeStr, 10);
    let fraction = parseInt(fractionStr || '0', 10);

    // Use two most-significant digits, e.g. 1 -> 10, 12 -> 12, 123 -> 12.3, 1234 -> 12.34, etc
    fraction = fraction * Math.pow(10, 2 - fraction.toString().length);

    return whole * 100 + fraction;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const template = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus.dataset.currency || '';

    return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (typeof placeholder !== 'string') return '';
      if (placeholder === 'currency') return currency;

      let thousandsSeparator = ',';
      let decimalSeparator = '.';
      let precision = CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;

      if (placeholder === 'amount') {
        // Check first since it's the most common, use defaults.
      } else if (placeholder === 'amount_no_decimals') {
        precision = 0;
      } else if (placeholder === 'amount_with_comma_separator') {
        thousandsSeparator = '.';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
        // Weirdly, this is correct. It uses amount_with_comma_separator's
        // behaviour but removes decimals, resulting in an unintuitive
        // output that can't possibly include commas, despite the name.
        thousandsSeparator = '.';
        precision = 0;
      } else if (placeholder === 'amount_no_decimals_with_space_separator') {
        thousandsSeparator = ' ';
        precision = 0;
      } else if (placeholder === 'amount_with_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_with_period_and_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = '.';
      } else if (placeholder === 'amount_with_apostrophe_separator') {
        thousandsSeparator = "'";
        decimalSeparator = '.';
      }

      return this.#formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision);
    });
  }

  /**
   * Formats money in cents
   * @param {number} moneyValue - The money value in cents (hundredths of one major currency unit)
   * @param {string} thousandsSeparator - The thousands separator
   * @param {string} decimalSeparator - The decimal separator
   * @param {number} precision - The precision
   * @returns {string} The formatted money value
   */
  #formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision) {
    const roundedNumber = (moneyValue / 100).toFixed(precision);

    let [a, b] = roundedNumber.split('.');
    if (!a) a = '0';
    if (!b) b = '';

    // Split by groups of 3 digits
    a = a.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);

    if (precision <= 0) return a;
    const decimal = b.replace(/0+$/, ''); // Remove trailing zeros
    return decimal ? a + decimalSeparator + decimal : a;
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.innerHTML = '';
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}

/**
 * Default currency decimals used in most currenies
 * @constant {number}
 */
const DEFAULT_CURRENCY_DECIMALS = 2;

/**
 * Decimal precision for currencies that have a non-default precision
 * @type {Record<string, number>}
 */
const CURRENCY_DECIMALS = {
  BHD: 3,
  BIF: 0,
  BYR: 0,
  CLF: 4,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  IQD: 3,
  ISK: 0,
  JOD: 3,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  KWD: 3,
  LYD: 3,
  MRO: 5,
  OMR: 3,
  PYG: 0,
  RWF: 0,
  TND: 3,
  UGX: 0,
  UYI: 0,
  UYW: 4,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XAG: 0,
  XAU: 0,
  XBA: 0,
  XBB: 0,
  XBC: 0,
  XBD: 0,
  XDR: 0,
  XOF: 0,
  XPD: 0,
  XPF: 0,
  XPT: 0,
  XSU: 0,
  XTS: 0,
  XUA: 0,
};
